/**
 * VoiceTestControls Component
 * Interface for testing text-to-speech with custom or sample text
 */

import React, { useState } from 'react';
import { FiPlay, FiLoader } from 'react-icons/fi';
import type { Voice } from '../../types/voice.types';

interface VoiceTestControlsProps {
  onTest: (text: string) => void;
  isTesting: boolean;
  disabled?: boolean;
  selectedVoice: Voice | null;
  className?: string;
}

const SAMPLE_TEXTS = [
  "Hello! I'm your AI assistant. How can I help you today?",
  "Thank you for calling. Let me help you with your inquiry.",
  "Your order has been confirmed and will be shipped within 24 hours.",
  "I understand your concern. Let me find the best solution for you.",
  "The weather forecast shows sunny skies with a high of 75 degrees."
];

export const VoiceTestControls: React.FC<VoiceTestControlsProps> = ({
  onTest,
  isTesting,
  disabled = false,
  selectedVoice,
  className = ''
}) => {
  const [customText, setCustomText] = useState('');
  const [selectedSample, setSelectedSample] = useState(0);
  const [useCustom, setUseCustom] = useState(false);

  const handleTest = () => {
    const textToTest = useCustom ? customText : SAMPLE_TEXTS[selectedSample];
    if (textToTest.trim()) {
      onTest(textToTest);
    }
  };

  const canTest = !disabled && !isTesting && (useCustom ? customText.trim().length > 0 : true);

  return (
    <div className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 ${className}`}>
      <div className="p-4 border-b border-slate-700">
        <h3 className="text-lg font-semibold text-white">Test Voice</h3>
        {selectedVoice && (
          <p className="text-sm text-gray-400 mt-1">
            Testing: <span className="text-blue-400">{selectedVoice.name}</span>
          </p>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Toggle between sample and custom */}
        <div className="flex gap-2">
          <button
            onClick={() => setUseCustom(false)}
            className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
              !useCustom
                ? 'bg-blue-500 text-white'
                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
            }`}
          >
            Sample Text
          </button>
          <button
            onClick={() => setUseCustom(true)}
            className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
              useCustom
                ? 'bg-blue-500 text-white'
                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
            }`}
          >
            Custom Text
          </button>
        </div>

        {/* Sample Text Selection */}
        {!useCustom && (
          <div>
            <label htmlFor="sample-text" className="block text-sm font-medium text-gray-300 mb-2">
              Choose Sample
            </label>
            <select
              id="sample-text"
              value={selectedSample}
              onChange={(e) => setSelectedSample(parseInt(e.target.value))}
              className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={disabled}
            >
              {SAMPLE_TEXTS.map((text, index) => (
                <option key={index} value={index}>
                  {text.substring(0, 50)}...
                </option>
              ))}
            </select>
            <div className="mt-2 p-3 bg-slate-900/50 rounded-lg">
              <p className="text-sm text-gray-400">{SAMPLE_TEXTS[selectedSample]}</p>
            </div>
          </div>
        )}

        {/* Custom Text Input */}
        {useCustom && (
          <div>
            <label htmlFor="custom-text" className="block text-sm font-medium text-gray-300 mb-2">
              Enter Text
            </label>
            <textarea
              id="custom-text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Type the text you want to hear..."
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              disabled={disabled}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Max 500 characters</span>
              <span>{customText.length} / 500</span>
            </div>
          </div>
        )}

        {/* Test Button */}
        <button
          onClick={handleTest}
          disabled={!canTest}
          className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
            canTest
              ? 'bg-blue-500 hover:bg-blue-600 text-white'
              : 'bg-slate-700 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isTesting ? (
            <>
              <FiLoader className="animate-spin" size={20} />
              <span>Playing...</span>
            </>
          ) : (
            <>
              <FiPlay size={20} />
              <span>Test Voice</span>
            </>
          )}
        </button>

        {/* Help Text */}
        {disabled && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <p className="text-xs text-yellow-400">
              Please select a voice first to test text-to-speech
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceTestControls;
